import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import LoadingRenderer from '../../../components/LoadingRenderer';
import { PageHeader } from '../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { HEADER_BUTTON_BLUE, HEADER_BUTTON_WHITE } from '../../../constants';
import Strings from '../../../Strings';
import actions from './redux/actions';
import {
  formatIssueDate,
  getAssignableUserLabel,
  getResponseItems,
  ISSUE_CREATE_SEVERITY_OPTIONS,
  ORGLESS_SUPERUSER_ROLE_TITLE,
} from './shared';

import './MessagesOverview.scss';
import './IssuesOverview.scss';

function IssueMeta({ label, value }) {
  return (
    <div className="health-issues-detail__property-row">
      <div className="health-issues-detail__property-label">{label}</div>
      <div className="health-issues-detail__property-value">{value || '-'}</div>
    </div>
  );
}

IssueMeta.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function IssueDetailsPage({
  addIssueComment,
  editIssue,
  getAdminRoles,
  getAdminUsers,
  getIssue,
  issueId,
  match,
  onNavigate,
}) {
  const resolvedIssueId = issueId || match?.params?.issueId;
  const [issue, setIssue] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [assignableUsers, setAssignableUsers] = React.useState([]);
  const [assignableUsersError, setAssignableUsersError] = React.useState('');
  const [isEditing, setIsEditing] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [commentDraft, setCommentDraft] = React.useState('');
  const [isSavingComment, setIsSavingComment] = React.useState(false);
  const [editIssueError, setEditIssueError] = React.useState('');
  const [editIssueForm, setEditIssueForm] = React.useState({
    title: '',
    description: '',
    deviceId: '',
    category: '',
    severity: 'low',
    assignedTo: '',
  });

  const loadIssue = React.useCallback(() => {
    if (!resolvedIssueId) {
      return;
    }

    setIsLoading(true);
    setError('');

    getIssue(resolvedIssueId)
      .then(resp => {
        const nextIssue = resp?.response || null;
        setIssue(nextIssue);
        setIsLoading(false);
      })
      .catch(() => {
        setError(Strings.superuserHealthLoadIssueDetailsError);
        setIsLoading(false);
      });
  }, [getIssue, resolvedIssueId]);

  React.useEffect(() => {
    loadIssue();
  }, [loadIssue]);

  React.useEffect(() => {
    let isMounted = true;

    const loadAssignableUsers = async () => {
      try {
        const rolesResp = await getAdminRoles({
          limit: 200,
          search: ORGLESS_SUPERUSER_ROLE_TITLE,
          sortColumn: 'title',
          sortType: 'ascending',
        });
        const role = getResponseItems(rolesResp).find(item => item?.title === ORGLESS_SUPERUSER_ROLE_TITLE);
        if (!role?.role_uuid) {
          if (isMounted) {
            setAssignableUsersError(Strings.superuserHealthLoadAssigneeRoleError);
          }
          return;
        }

        const usersResp = await getAdminUsers({
          limit: 200,
          role: role.role_uuid,
          sortColumn: 'first_name',
          sortType: 'ascending',
        });

        if (isMounted) {
          setAssignableUsers(getResponseItems(usersResp));
          setAssignableUsersError('');
        }
      } catch (loadError) {
        if (isMounted) {
          setAssignableUsers([]);
          setAssignableUsersError(Strings.superuserHealthLoadAssignableUsersError);
        }
      }
    };

    loadAssignableUsers();

    return () => {
      isMounted = false;
    };
  }, [getAdminRoles, getAdminUsers]);

  React.useEffect(() => {
    if (!issue) {
      return;
    }

    const assignedUser = assignableUsers.find(user => user?.username === issue.assigned_to);
    setEditIssueForm({
      title: issue.title || '',
      description: issue.description || '',
      deviceId: issue.device_id || '',
      category: issue.category || '',
      severity: issue.severity || 'low',
      assignedTo: assignedUser?.unguessable_id || '',
    });
  }, [assignableUsers, issue]);

  const onSave = React.useCallback(() => {
    if (!issue?.issue_id) {
      return;
    }

    const currentAssignedToUuid = assignableUsers.find(user => user?.username === issue.assigned_to)?.unguessable_id || '';
    const payload = {
      title: editIssueForm.title.trim(),
      description: editIssueForm.description.trim(),
      device_id: editIssueForm.deviceId.trim(),
      category: editIssueForm.category.trim(),
      severity: editIssueForm.severity,
    };

    if (!payload.title || !payload.category) {
      setEditIssueError(Strings.superuserHealthTitleAndCategoryRequired);
      return;
    }

    if (editIssueForm.assignedTo !== currentAssignedToUuid) {
      payload.assigned_to = editIssueForm.assignedTo || null;
    }

    setIsSaving(true);
    setEditIssueError('');

    editIssue(issue.issue_id, payload)
      .then(resp => {
        setIssue(resp?.response || issue);
        setIsSaving(false);
        setIsEditing(false);
      })
      .catch(() => {
        setEditIssueError(Strings.superuserHealthUpdateIssueError);
        setIsSaving(false);
      });
  }, [assignableUsers, editIssue, editIssueForm, issue]);

  const onToggleResolved = React.useCallback(() => {
    if (!issue?.issue_id || isSaving) {
      return;
    }

    setIsSaving(true);
    editIssue(issue.issue_id, { resolved: !issue.resolved_at })
      .then(resp => {
        setIssue(resp?.response || issue);
        setIsSaving(false);
      })
      .catch(() => {
        setEditIssueError(Strings.superuserHealthUpdateIssueError);
        setIsSaving(false);
      });
  }, [editIssue, isSaving, issue]);

  const onAddComment = React.useCallback(() => {
    const trimmed = commentDraft.trim();
    if (!issue?.issue_id || !trimmed || isSavingComment) {
      return;
    }

    setIsSavingComment(true);
    addIssueComment(issue.issue_id, { note: trimmed })
      .then(() => {
        setCommentDraft('');
        setIsSavingComment(false);
        loadIssue();
      })
      .catch(() => {
        setEditIssueError(Strings.superuserHealthAddCommentError);
        setIsSavingComment(false);
      });
  }, [addIssueComment, commentDraft, isSavingComment, issue, loadIssue]);

  const headerActions = isEditing ? (
    <>
      <Button
        class={HEADER_BUTTON_WHITE}
        onClick={() => setIsEditing(false)}
        title={Strings.cancel}
        disabled={isSaving}
      />
      <Button
        class={HEADER_BUTTON_DARK_BLUE}
        onClick={onSave}
        title={Strings.save}
        disabled={isSaving}
      />
    </>
  ) : (
    <>
      <Button class={HEADER_BUTTON_BLUE} onClick={() => setIsEditing(true)} title={Strings.edit} disabled={isSaving} />
      <Button
        class={HEADER_BUTTON_DARK_BLUE}
        onClick={onToggleResolved}
        title={issue?.resolved_at ? Strings.superuserHealthReopenIssue : Strings.superuserHealthMarkResolved}
        disabled={isSaving}
      />
    </>
  );

  return (
    <>
      <PageHeader
        onBackClick={() => onNavigate('/superuser-health/issues')}
        left={issue?.title || Strings.superuserHealthIssueDetailsTitle}
        right={headerActions}
      />

      {assignableUsersError ? <div className="health-messages-error">{assignableUsersError}</div> : null}
      {error ? <div className="health-messages-error">{error}</div> : null}
      {editIssueError ? <div className="health-messages-error">{editIssueError}</div> : null}

      <LoadingRenderer loading={isLoading}>
        {issue ? (
          <div className="health-issues__detail-page">
            <div className="health-issues-detail">
              {isEditing ? (
                <div className="health-issues-detail__edit-form">
                  <div className="health-issues__form-grid">
                    <div className="health-issues__form-field health-issues__form-field--full">
                      <div className="health-issues__form-label">{Strings.title}</div>
                      <input
                        id="edit-issue-title-input"
                        type="text"
                        value={editIssueForm.title}
                        onChange={event => setEditIssueForm(prev => ({ ...prev, title: event.target.value }))}
                      />
                    </div>
                    <div className="health-issues__form-field health-issues__form-field--full">
                      <div className="health-issues__form-label">{Strings.description}</div>
                      <textarea
                        id="edit-issue-description-input"
                        value={editIssueForm.description}
                        onChange={event => setEditIssueForm(prev => ({ ...prev, description: event.target.value }))}
                        rows={5}
                      />
                    </div>
                    <div className="health-issues__form-field">
                      <div className="health-issues__form-label">{Strings.deviceId}</div>
                      <input
                        id="edit-issue-device-id-input"
                        type="text"
                        value={editIssueForm.deviceId}
                        onChange={event => setEditIssueForm(prev => ({ ...prev, deviceId: event.target.value }))}
                      />
                    </div>
                    <div className="health-issues__form-field">
                      <div className="health-issues__form-label">{Strings.category}</div>
                      <input
                        id="edit-issue-category-input"
                        type="text"
                        value={editIssueForm.category}
                        onChange={event => setEditIssueForm(prev => ({ ...prev, category: event.target.value }))}
                      />
                    </div>
                    <div className="health-issues__form-field">
                      <div className="health-issues__form-label">{Strings.severity}</div>
                      <select
                        id="edit-issue-severity-input"
                        value={editIssueForm.severity}
                        onChange={event => setEditIssueForm(prev => ({ ...prev, severity: event.target.value }))}
                      >
                        {ISSUE_CREATE_SEVERITY_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="health-issues__form-field">
                      <div className="health-issues__form-label">{Strings.assignedTo}</div>
                      <select
                        id="edit-issue-assigned-to-input"
                        value={editIssueForm.assignedTo}
                        onChange={event => setEditIssueForm(prev => ({ ...prev, assignedTo: event.target.value }))}
                      >
                        <option value="">{Strings.none}</option>
                        {assignableUsers.map(user => (
                          <option key={user.unguessable_id} value={user.unguessable_id}>
                            {getAssignableUserLabel(user)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="health-issues-detail__content">
                  <div className="health-issues-detail__description">
                    <div className="health-issues-detail__description-label">{Strings.description}</div>
                    <div className="health-issues-detail__description-value">{issue.description || '-'}</div>
                  </div>

                  <div className="health-issues-detail__meta-column">
                    <div className="health-issues-detail__property-card">
                      <div className="health-issues-detail__property-card-title">{Strings.superuserHealthProperties}</div>
                      <IssueMeta label={Strings.createdAt} value={formatIssueDate(issue.created_at)} />
                      <IssueMeta label={Strings.createdBy} value={issue.created_by} />
                      <IssueMeta label={Strings.assignedTo} value={issue.assigned_to} />
                      <IssueMeta label={Strings.superuserHealthResolvedAt} value={formatIssueDate(issue.resolved_at)} />
                      <IssueMeta label={Strings.superuserHealthResolvedBy} value={issue.resolved_by} />
                    </div>
                    <div className="health-issues-detail__property-card">
                      <div className="health-issues-detail__property-card-title">{Strings.superuserHealthLabels}</div>
                      <IssueMeta label={Strings.category} value={issue.category} />
                      <IssueMeta label={Strings.severity} value={issue.severity} />
                      <IssueMeta
                        label={Strings.superuserHealthCustomerCase}
                        value={issue.customer_case ? Strings.yes : Strings.no}
                      />
                      <IssueMeta label={Strings.deviceId} value={issue.device_id} />
                    </div>
                  </div>
                </div>
              )}

              <div className="health-issues-detail__comments">
                <div className="health-issues-detail__comments-title">{Strings.comments}</div>
                {issue.comments?.length ? (
                  <div className="health-issues-detail__comment-list">
                    {issue.comments.map(comment => (
                      <div key={comment.comment_id} className="health-issues-detail__comment">
                        <div className="health-issues-detail__comment-top">
                          <span>{comment.created_by || '-'}</span>
                          <span>{formatIssueDate(comment.created_at)}</span>
                        </div>
                        <div className="health-issues-detail__comment-note">{comment.note || '-'}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="health-messages-empty">{Strings.superuserHealthNoComments}</div>
                )}

                <div className="health-issues-detail__comment-form">
                  <div className="health-issues-detail__section-label">{Strings.superuserHealthAddComment}</div>
                  <textarea
                    id="health-issues-comment"
                    value={commentDraft}
                    onChange={event => setCommentDraft(event.target.value)}
                    rows={4}
                    placeholder={Strings.superuserHealthCommentPlaceholder}
                  />
                  <div className="health-issues-detail__comment-actions">
                    <Button
                      class={HEADER_BUTTON_DARK_BLUE}
                      onClick={onAddComment}
                      title={Strings.superuserHealthAddComment}
                      disabled={!commentDraft.trim() || isSavingComment}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </LoadingRenderer>
    </>
  );
}

IssueDetailsPage.propTypes = {
  addIssueComment: PropTypes.func.isRequired,
  editIssue: PropTypes.func.isRequired,
  getAdminRoles: PropTypes.func.isRequired,
  getAdminUsers: PropTypes.func.isRequired,
  getIssue: PropTypes.func.isRequired,
  issueId: PropTypes.string,
  match: PropTypes.shape({
    params: PropTypes.shape({
      issueId: PropTypes.string,
    }),
  }),
  onNavigate: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  addIssueComment: (id, data) => dispatch(actions.addIssueComment(id, data)),
  editIssue: (id, data) => dispatch(actions.editIssue(id, data)),
  getAdminRoles: params => dispatch(actions.getAdminRoles(params)),
  getAdminUsers: params => dispatch(actions.getAdminUsers(params)),
  getIssue: id => dispatch(actions.getIssue(id)),
  onNavigate: path => dispatch(push(path)),
});

export default connect(null, mapDispatchToProps)(IssueDetailsPage);
